import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Mail, RefreshCw, Image as ImageIcon,
  DollarSign, CheckCircle, AlertCircle, Paperclip, Video, Mic, File, CheckSquare, Square
} from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const SubmissionDocumentTab = ({ claim, user }) => {
  const [document, setDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [allPhotos, setAllPhotos] = useState([]);
  const [allDocuments, setAllDocuments] = useState([]);
  const [includedItems, setIncludedItems] = useState(new Set());

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

  useEffect(() => {
    if (claim?.id && user?.id) {
      loadSubmissionDocument();
      loadAllAttachments();
    }
  }, [claim?.id, user?.id]);

  const loadSubmissionDocument = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/claims/${claim.id}/submission-document`,
        { params: { user_id: user.id } }
      );
      setDocument(response.data);
    } catch (error) {
      console.error('Error loading submission document:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllAttachments = async () => {
    try {
      // Load photos
      const photosRes = await axios.get(
        `${BACKEND_URL}/api/claims/${claim.id}/photos`,
        { params: { user_id: user.id } }
      );
      setAllPhotos(photosRes.data || []);
      
      // Load all claim documents (photos, lead files, field reports, etc.)
      const docsRes = await axios.get(
        `${BACKEND_URL}/api/claims/${claim.id}/documents`,
        { params: { user_id: user.id } }
      ).catch(() => ({ data: [] }));
      
      setAllDocuments(docsRes.data || []);
      
      // Default: include everything
      const allIds = new Set();
      (photosRes.data || []).forEach(p => allIds.add(p.id));
      (docsRes.data || []).forEach(d => allIds.add(d.id));
      setIncludedItems(allIds);
    } catch (error) {
      console.error('Error loading attachments:', error);
    }
  };

  const toggleItem = (id) => {
    setIncludedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    const allIds = new Set();
    allPhotos.forEach(p => allIds.add(p.id));
    allDocuments.forEach(d => allIds.add(d.id));
    setIncludedItems(allIds);
  };

  const deselectAll = () => {
    setIncludedItems(new Set());
  };

  const getFileIcon = (item) => {
    const type = item.file_type || item.category || '';
    if (type.includes('image') || type === 'photo') return <ImageIcon className="w-4 h-4 text-blue-500" />;
    if (type.includes('video')) return <Video className="w-4 h-4 text-purple-500" />;
    if (type.includes('audio')) return <Mic className="w-4 h-4 text-green-500" />;
    if (type.includes('pdf') || type === 'document') return <FileText className="w-4 h-4 text-red-500" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/claims/${claim.id}/submission-document/regenerate`,
        { included_document_ids: Array.from(includedItems) },
        { params: { user_id: user.id } }
      );
      setDocument(response.data);
      toast.success('Document regenerated with selected attachments');
    } catch (error) {
      console.error('Error regenerating document:', error);
      toast.error('Failed to regenerate document');
    } finally {
      setIsRegenerating(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Generating submission document...</p>
        </div>
      </div>
    );
  }

  if (!document) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Document Available</h3>
          <p className="text-gray-600 mb-4">Unable to generate submission document.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex gap-3">
        <Button
          onClick={handleRegenerate}
          disabled={isRegenerating}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${isRegenerating ? 'animate-spin' : ''}`} />
          {isRegenerating ? 'Regenerating...' : 'Regenerate Document'}
        </Button>
        
        <Button
          onClick={() => {
            if (!document) return;
            const h = document.header || {};
            const d = document.damage_assessment || {};
            let text = `INSURANCE CLAIM SUBMISSION DOCUMENT\n${'='.repeat(50)}\n\n`;
            text += `Contractor: ${h.contractor_name || ''}\n`;
            text += `License: ${h.contractor_license || ''}\n`;
            text += `Phone: ${h.contractor_phone || ''}\n`;
            text += `Email: ${h.contractor_email || ''}\n\n`;
            text += `Property: ${h.property_address || ''}\n`;
            text += `Homeowner: ${h.homeowner_name || ''}\n`;
            text += `Claim #: ${h.claim_number || ''}\n`;
            text += `Policy #: ${h.policy_number || ''}\n`;
            text += `Storm Date: ${h.storm_date || ''}\n\n`;
            text += `DAMAGE ASSESSMENT\n${'-'.repeat(30)}\n`;
            text += `Roof Type: ${d.roof_type || ''}\n`;
            text += `Total Area: ${d.total_area || ''}\n`;
            text += `Damage Extent: ${d.damage_extent || ''}\n\n`;
            if (d.damage_details) text += `Details:\n${d.damage_details}\n\n`;
            if (document.scope_of_work) {
              text += `SCOPE OF WORK\n${'-'.repeat(30)}\n`;
              (document.scope_of_work.line_items || []).forEach(item => {
                text += `${item.description}: ${item.quantity} ${item.unit} @ $${item.unit_price} = $${item.total}\n`;
              });
              text += `\nTotal: $${document.scope_of_work.total_cost || ''}\n\n`;
            }
            if (document.supporting_evidence) text += `SUPPORTING EVIDENCE\n${'-'.repeat(30)}\n${document.supporting_evidence}\n\n`;
            if (document.closing_statement) text += `CLOSING\n${'-'.repeat(30)}\n${document.closing_statement}\n`;
            const blob = new Blob([text], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = window.document.createElement('a');
            a.href = url;
            a.download = `claim-submission-${claim?.claim_number || claim?.id || 'document'}.txt`;
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center"
          data-testid="download-submission-doc"
        >
          <Download className="w-4 h-4 mr-2" />
          Download Report
        </Button>
        
        <Button
          onClick={async () => {
            const email = prompt('Enter the adjuster or customer email address:');
            if (!email) return;
            try {
              const token = localStorage.getItem('rooftrack_token');
              await axios.post(`${BACKEND_URL}/api/claims/${claim?.id}/email-document`, 
                { to_email: email, document_type: 'submission' },
                { headers: { Authorization: `Bearer ${token}` }}
              );
              toast.success(`Document emailed to ${email}`);
            } catch (err) {
              toast.error(err.response?.data?.detail || 'Failed to send email');
            }
          }}
          className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center"
          data-testid="email-submission-doc"
        >
          <Mail className="w-4 h-4 mr-2" />
          Email to Adjuster
        </Button>
      </div>

      {/* Document Preview */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-8 space-y-6">
          {/* Header */}
          <div className="border-b-2 border-gray-300 pb-4">
            <h1 className="text-2xl font-bold text-gray-900">{document.header?.company_name || 'Your Company'}</h1>
            <p className="text-lg text-gray-700">{document.header?.contractor_name}</p>
            <p className="text-sm text-gray-600">{document.header?.contractor_email}</p>
            <p className="text-sm text-gray-600">{document.header?.contractor_phone}</p>
          </div>

          {/* Property & Insurance Info */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-semibold text-gray-700">Property:</p>
              <p className="text-base text-gray-900">{document.property?.address}</p>
              <p className="text-sm text-gray-600 mt-2">Date: {new Date(document.generated_at).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700">Insurance:</p>
              <p className="text-base text-gray-900">{document.insurance?.company}</p>
              <p className="text-sm text-gray-600">Claimant: {document.property?.customer_name}</p>
              <p className="text-sm text-gray-600">Claim No: {document.insurance?.claim_number}</p>
            </div>
          </div>

          {/* Title */}
          <div className="border-t-2 border-gray-300 pt-4">
            <h2 className="text-xl font-bold text-gray-900 text-center">SCOPE OF WORK & ESTIMATE</h2>
          </div>

          {/* Work Sections */}
          {document.work_sections?.map((section, idx) => (
            <div key={idx} className="space-y-3">
              <h3 className="text-lg font-bold text-gray-900 border-b border-gray-300 pb-2">
                {idx + 1}. {section.title}
              </h3>
              
              {/* Line Items Table */}
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Description</th>
                    <th className="text-center py-2 px-3 font-semibold text-gray-700">Qty</th>
                    <th className="text-center py-2 px-3 font-semibold text-gray-700">Unit</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Unit Price</th>
                    <th className="text-right py-2 px-3 font-semibold text-gray-700">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Materials Section Header */}
                  {section.line_items?.some(item => item.category === 'materials') && (
                    <tr className="bg-blue-50">
                      <td colSpan="5" className="py-2 px-3 font-bold text-blue-800 text-sm">
                        MATERIALS
                      </td>
                    </tr>
                  )}
                  {/* Materials Line Items */}
                  {section.line_items?.filter(item => item.category === 'materials' || !item.category).map((item, lineIdx) => (
                    item.category !== 'labor' && (
                      <tr key={`mat-${lineIdx}`} className="border-b border-gray-200">
                        <td className="py-2 px-3 text-gray-800">{item.description}</td>
                        <td className="py-2 px-3 text-center text-gray-800">{Number(item.quantity).toFixed(2)}</td>
                        <td className="py-2 px-3 text-center text-gray-600">{item.unit}</td>
                        <td className="py-2 px-3 text-right text-gray-800">{formatCurrency(item.unit_price)}</td>
                        <td className="py-2 px-3 text-right font-semibold text-gray-900">{formatCurrency(item.total)}</td>
                      </tr>
                    )
                  ))}
                  {/* Labor Section Header */}
                  {section.line_items?.some(item => item.category === 'labor') && (
                    <tr className="bg-green-50">
                      <td colSpan="5" className="py-2 px-3 font-bold text-green-800 text-sm">
                        LABOR
                      </td>
                    </tr>
                  )}
                  {/* Labor Line Items */}
                  {section.line_items?.filter(item => item.category === 'labor').map((item, lineIdx) => (
                    <tr key={`lab-${lineIdx}`} className="border-b border-gray-200">
                      <td className="py-2 px-3 text-gray-800">{item.description}</td>
                      <td className="py-2 px-3 text-center text-gray-800">{Number(item.quantity).toFixed(2)}</td>
                      <td className="py-2 px-3 text-center text-gray-600">{item.unit}</td>
                      <td className="py-2 px-3 text-right text-gray-800">{formatCurrency(item.unit_price)}</td>
                      <td className="py-2 px-3 text-right font-semibold text-gray-900">{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Section Totals */}
              <div className="bg-gray-50 p-4 rounded space-y-2">
                {section.materials_subtotal !== undefined && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Materials Subtotal:</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(section.materials_subtotal)}</span>
                  </div>
                )}
                {section.labor_subtotal !== undefined && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Labor Subtotal:</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(section.labor_subtotal)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm border-t border-gray-200 pt-2">
                  <span className="text-gray-700">Subtotal (Materials + Labor):</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(section.subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Overhead & Profit ({section.overhead_profit_percent}%):</span>
                  <span className="font-semibold text-gray-900">{formatCurrency(section.overhead_profit_amount)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t border-gray-300 pt-2">
                  <span className="text-gray-900">TOTAL {section.title.toUpperCase()}:</span>
                  <span className="text-orange-600">{formatCurrency(section.total)}</span>
                </div>
              </div>
            </div>
          ))}

          {/* Attachments — Include/Exclude Selection */}
          {(allPhotos.length > 0 || allDocuments.length > 0) && (
            <div className="space-y-4 border-t-2 border-gray-300 pt-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900 flex items-center">
                  <Paperclip className="w-5 h-5 mr-2" />
                  CLAIM ATTACHMENTS
                </h3>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-xs text-blue-600 hover:underline" data-testid="select-all-docs">Select All</button>
                  <span className="text-gray-300">|</span>
                  <button onClick={deselectAll} className="text-xs text-red-600 hover:underline" data-testid="deselect-all-docs">Deselect All</button>
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Check the items to include when submitting claim information. Unchecked items will be excluded.
              </p>
              <p className="text-sm font-medium text-gray-700">
                {includedItems.size} of {allPhotos.length + allDocuments.length} items included
              </p>

              {/* Photos grid */}
              {allPhotos.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <ImageIcon className="w-4 h-4" /> Photos ({allPhotos.filter(p => includedItems.has(p.id)).length}/{allPhotos.length})
                  </p>
                  <div className="grid grid-cols-4 gap-3">
                    {allPhotos.map(photo => (
                      <div
                        key={photo.id}
                        onClick={() => toggleItem(photo.id)}
                        className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                          includedItems.has(photo.id)
                            ? 'border-orange-500 ring-2 ring-orange-200'
                            : 'border-gray-300 opacity-50 hover:opacity-75'
                        }`}
                        data-testid={`doc-toggle-${photo.id}`}
                      >
                        <img
                          src={photo.supabase_url}
                          alt={photo.file_name}
                          className="w-full h-28 object-cover"
                        />
                        <div className="absolute top-1.5 right-1.5">
                          {includedItems.has(photo.id) ? (
                            <CheckSquare className="w-5 h-5 text-orange-500 bg-white rounded" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400 bg-white rounded" />
                          )}
                        </div>
                        <p className="text-[10px] text-gray-600 p-1.5 bg-white truncate">
                          {photo.description || photo.file_name}
                        </p>
                        {photo.source === 'lead_files' && (
                          <span className="absolute bottom-7 left-1 px-1.5 py-0.5 bg-blue-500 text-white text-[9px] rounded">Lead</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Other documents list */}
              {allDocuments.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-1">
                    <FileText className="w-4 h-4" /> Documents & Reports ({allDocuments.filter(d => includedItems.has(d.id)).length}/{allDocuments.length})
                  </p>
                  <div className="space-y-1.5">
                    {allDocuments.map(doc => (
                      <div
                        key={doc.id}
                        onClick={() => toggleItem(doc.id)}
                        className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                          includedItems.has(doc.id)
                            ? 'border-orange-300 bg-orange-50'
                            : 'border-gray-200 bg-gray-50 opacity-60 hover:opacity-80'
                        }`}
                        data-testid={`doc-toggle-${doc.id}`}
                      >
                        {includedItems.has(doc.id) ? (
                          <CheckSquare className="w-5 h-5 text-orange-500 flex-shrink-0" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                        {getFileIcon(doc)}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{doc.file_name || doc.filename || 'Document'}</p>
                          <p className="text-xs text-gray-500">
                            {doc.category || doc.file_type || 'file'}
                            {doc.source === 'lead_files' && <span className="ml-2 text-blue-600">from Lead</span>}
                            {doc.uploaded_at && <span className="ml-2">{new Date(doc.uploaded_at).toLocaleDateString()}</span>}
                          </p>
                        </div>
                        {doc.supabase_url && (
                          <a href={doc.supabase_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} className="text-gray-400 hover:text-blue-600">
                            <Download className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Summary Table */}
          <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
            <h3 className="text-lg font-bold text-blue-900 mb-4">ESTIMATE SUMMARY</h3>
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-blue-300">
                  <th className="text-left py-2 font-semibold text-blue-900">Item</th>
                  <th className="text-right py-2 font-semibold text-blue-900">Amount</th>
                </tr>
              </thead>
              <tbody>
                {document.work_sections?.map((section, idx) => (
                  <tr key={idx} className="border-b border-blue-200">
                    <td className="py-2 text-blue-800">{section.title} (with O&P)</td>
                    <td className="py-2 text-right text-blue-800">{formatCurrency(section.total)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-blue-300">
                  <td className="py-3 text-xl font-bold text-blue-900">COMBINED ESTIMATE</td>
                  <td className="py-3 text-right text-2xl font-bold text-orange-600">
                    {formatCurrency(document.total_estimate)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Notes */}
          {document.notes && document.notes.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-lg font-bold text-gray-900">NOTES:</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                {document.notes.map((note, idx) => (
                  <li key={idx}>{note}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Footer */}
          <div className="border-t-2 border-gray-300 pt-4 text-sm text-gray-600">
            <p><strong>Prepared by:</strong> {document.header?.contractor_name}</p>
            <p><strong>Contact:</strong> {document.header?.contractor_email}</p>
            <p><strong>Date:</strong> {new Date(document.generated_at).toLocaleDateString()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDocumentTab;
